import { useState } from 'react'
import { useSearchParams } from 'react-router'
import { useAuth } from '@/hooks/useAuth'
import { OpeningBalanceModal } from '@/components/accounting/OpeningBalanceModal'
import { formatBaht } from '@/lib/money'
import { useTranslation } from 'react-i18next'
import i18n from '@/i18n'
import { BookOpen, Plus, Sprout, Edit, Trash2, Landmark } from 'lucide-react'
import { PageHeader, Breadcrumbs } from '@/components/layout'
import { Button, Card, Badge, EmptyState, Skeleton, Modal, Input, Select, Checkbox, Alert } from '@/components/ui'
import {
  useGlAccounts,
  useBankOpeningBalance,
  useCreateGlAccount,
  useUpdateGlAccount,
  useDeleteGlAccount,
  useSeedGlAccounts,
  type CreateGlAccountInput,
} from '@/hooks/useAccounting'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import type { GlAccount } from '@/types'

// WEB-M9: Badge component variants (raw .badge crops Thai descenders)
const accountTypeVariants: Record<string, 'info' | 'error' | 'secondary' | 'success' | 'warning'> = {
  asset: 'info',
  liability: 'error',
  equity: 'secondary',
  revenue: 'success',
  expense: 'warning',
}

const glAccountFormSchema = z.object({
  code: z.string().min(1),
  name: z.string().min(1),
  name_th: z.string().optional(),
  account_type: z.enum(['asset', 'liability', 'equity', 'revenue', 'expense']),
  normal_balance: z.enum(['debit', 'credit']),
  parent_id: z.string().optional(),
  active: z.boolean().default(true),
})

type GlAccountFormData = z.infer<typeof glAccountFormSchema>

function buildTree(accounts: GlAccount[]): GlAccount[] {
  const map = new Map<number, GlAccount & { children: GlAccount[] }>()
  const roots: (GlAccount & { children: GlAccount[] })[] = []

  accounts.forEach((acc) => {
    map.set(acc.id, { ...acc, children: [] })
  })

  accounts.forEach((acc) => {
    const node = map.get(acc.id)!
    if (acc.parent_id && map.has(acc.parent_id)) {
      map.get(acc.parent_id)!.children.push(node)
    } else {
      roots.push(node)
    }
  })

  return roots
}

function flattenTree(
  nodes: (GlAccount & { children?: GlAccount[] })[],
  depth = 0
): { account: GlAccount; depth: number }[] {
  const result: { account: GlAccount; depth: number }[] = []
  for (const node of nodes) {
    result.push({ account: node, depth })
    if (node.children && node.children.length > 0) {
      result.push(...flattenTree(node.children as (GlAccount & { children?: GlAccount[] })[], depth + 1))
    }
  }
  return result
}

export function ChartOfAccountsPage() {
  const { t } = useTranslation('accounting')
  const { t: tc } = useTranslation('common')
  const { user } = useAuth()
  const [searchParams, setSearchParams] = useSearchParams()
  const canManageOpening = !!user && ['owner', 'admin', 'accountant'].includes(user.role)
  const openingBalance = useBankOpeningBalance(canManageOpening)
  const openingRequested = searchParams.get('opening_balance') === '1'

  const { data, isLoading, isFetching, error } = useGlAccounts()
  const createAccount = useCreateGlAccount()
  const updateAccount = useUpdateGlAccount()
  const deleteAccount = useDeleteGlAccount()
  const seedAccounts = useSeedGlAccounts()

  const [showFormModal, setShowFormModal] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [showSeedModal, setShowSeedModal] = useState(false)
  const [editingAccount, setEditingAccount] = useState<GlAccount | null>(null)
  const [deletingId, setDeletingId] = useState<number | null>(null)

  const accounts = data?.data || []
  const canSetOpening = canManageOpening && openingBalance.data?.posted === false && accounts.some((account) => account.code === '1120')
  const tree = buildTree(accounts)
  const flatList = flattenTree(tree)

  const accountOptions = [
    { value: '', label: t('filters.none_root_account') },
    ...accounts.map((a) => ({ value: String(a.id), label: `${a.code} - ${a.name}` })),
  ]

  const accountTypeOptions = [
    { value: 'asset', label: t('account_types.asset') },
    { value: 'liability', label: t('account_types.liability') },
    { value: 'equity', label: t('account_types.equity') },
    { value: 'revenue', label: t('account_types.revenue') },
    { value: 'expense', label: t('account_types.expense') },
  ]

  const normalBalanceOptions = [
    { value: 'debit', label: t('normal_balances.debit') },
    { value: 'credit', label: t('normal_balances.credit') },
  ]

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<GlAccountFormData>({
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    resolver: zodResolver(glAccountFormSchema) as any,
    defaultValues: { active: true, normal_balance: 'debit', account_type: 'asset' },
  })

  const openCreateModal = () => {
    setEditingAccount(null)
    reset({ active: true, normal_balance: 'debit', account_type: 'asset', code: '', name: '' })
    setShowFormModal(true)
  }

  const openEditModal = (account: GlAccount) => {
    setEditingAccount(account)
    reset({
      code: account.code,
      name: account.name,
      name_th: account.name_th ?? '',
      account_type: account.account_type,
      normal_balance: account.normal_balance,
      parent_id: account.parent_id ? String(account.parent_id) : '',
      active: account.active,
    })
    setShowFormModal(true)
  }

  const onSubmit = async (formData: GlAccountFormData) => {
    const input: CreateGlAccountInput = {
      code: formData.code,
      name: formData.name,
      name_th: formData.name_th || undefined,
      account_type: formData.account_type,
      normal_balance: formData.normal_balance,
      parent_id: formData.parent_id ? Number(formData.parent_id) : null,
      active: formData.active,
    }

    if (editingAccount) {
      await updateAccount.mutateAsync({ id: editingAccount.id, ...input })
    } else {
      await createAccount.mutateAsync(input)
    }
    setShowFormModal(false)
  }

  const handleDelete = async () => {
    if (!deletingId) return
    await deleteAccount.mutateAsync(deletingId)
    setShowDeleteModal(false)
    setDeletingId(null)
  }

  const setOpeningRequested = (open: boolean) => {
    setSearchParams((previous) => {
      const next = new URLSearchParams(previous)
      if (open) next.set('opening_balance', '1')
      else next.delete('opening_balance')
      return next
    }, { replace: true })
  }

  const closeOpeningModal = () => {
    setOpeningRequested(false)
    // Refresh after dismissal as well: another user may have posted while this
    // modal was open, in which case the POST reports a duplicate conflict.
    void openingBalance.refetch()
  }

  const handleSeed = async () => {
    await seedAccounts.mutateAsync()
    setShowSeedModal(false)
  }

  return (
    <div className="space-y-6">
      <Breadcrumbs items={[{ label: t('chart_of_accounts.title') }]} />

      <PageHeader
        title={t('chart_of_accounts.title')}
        description={t('chart_of_accounts.description')}
        isRefreshing={isFetching && !isLoading}
        primaryAction={{
          key: 'add-account',
          label: t('chart_of_accounts.add_account'),
          icon: Plus,
          onClick: openCreateModal,
        }}
        overflowActions={[
          {
            key: 'seed',
            label: t('chart_of_accounts.seed_accounts'),
            icon: Sprout,
            onClick: () => setShowSeedModal(true),
            loading: seedAccounts.isPending,
          },
        ]}
      />

      {canManageOpening && openingBalance.isError && (
        <Alert variant="error" role="alert">
          {t('opening_balance.status_error')}
          <Button variant="ghost" size="sm" onClick={() => { void openingBalance.refetch() }}>{tc('actions.retry')}</Button>
        </Alert>
      )}
      {canManageOpening && openingRequested && openingBalance.data?.posted === true && (
        <Alert variant="info" role="status">{t('errors.opening_balance_already_exists')}</Alert>
      )}

      {isLoading ? (
        <Card>
          <div className="space-y-3">
            {[...Array(8)].map((_, i) => (
              <Skeleton key={i} className="h-10" />
            ))}
          </div>
        </Card>
      ) : error ? (
        <Card>
          <EmptyState
            title={t('messages.error_loading')}
            description={t('messages.error_description')}
            action={<Button onClick={() => window.location.reload()}>{tc('actions.retry')}</Button>}
          />
        </Card>
      ) : flatList.length === 0 ? (
        <Card>
          <EmptyState
            icon={<BookOpen className="w-12 h-12" />}
            title={t('chart_of_accounts.empty_title')}
            description={t('chart_of_accounts.empty_description')}
            action={
              <Button
                leftIcon={<Sprout className="w-4 h-4" />}
                onClick={() => setShowSeedModal(true)}
              >
                {t('chart_of_accounts.seed_accounts')}
              </Button>
            }
          />
        </Card>
      ) : (
        <Card>
          <div className="overflow-x-auto">
            <table className="table table-sm w-full">
              <thead>
                <tr>
                  <th>{t('labels.code')}</th>
                  <th>{t('labels.name')}</th>
                  <th className="hidden md:table-cell">{t('labels.name_th')}</th>
                  {/* Below md the type badge sits under the name (2026-10-01):
                      four columns in a phone's 306px pushed the table 127px
                      past the edge, so the name was the first thing cut off. */}
                  <th className="hidden md:table-cell">{t('labels.account_type')}</th>
                  <th className="hidden lg:table-cell">{t('labels.normal_balance')}</th>
                  <th className="hidden lg:table-cell">{tc('labels.status')}</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {flatList.map(({ account, depth }) => (
                  <tr key={account.id} className="hover">
                    <td>
                      <div className="flex items-stretch">
                        {depth > 0 &&
                          Array.from({ length: depth }).map((_, i) => (
                            <span
                              key={i}
                              className={`coa-indent-block${i === depth - 1 ? ' coa-last-level' : ''}`}
                              aria-hidden="true"
                            />
                          ))}
                        <span className="font-mono text-sm">{account.code}</span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-stretch">
                        {depth > 0 &&
                          Array.from({ length: depth }).map((_, i) => (
                            <span
                              key={i}
                              className={`coa-indent-block${i === depth - 1 ? ' coa-last-level' : ''}`}
                              aria-hidden="true"
                            />
                          ))}
                        {/* Name and the system chip flow as text, so the chip
                            wraps under a long name instead of stretching to
                            the row's height as a flex item would (phone,
                            2026-10-01). */}
                        <div className="min-w-0">
                          <span className={depth === 0 ? 'font-semibold' : ''}>
                            {i18n.language === 'th' && account.name_th
                              ? account.name_th
                              : account.name}
                          </span>
                          {account.system && (
                            <Badge variant="ghost" size="xs" className="ml-2 align-middle">
                              {t('chart_of_accounts.system_account')}
                            </Badge>
                          )}
                          <div className="md:hidden mt-1">
                            <Badge size="sm" variant={accountTypeVariants[account.account_type] || 'ghost'}>
                              {t(`account_types.${account.account_type}`)}
                            </Badge>
                          </div>
                        </div>
                      </div>
                      {account.code === '1120' && canManageOpening && openingBalance.data && (
                        <p className="text-xs text-base-content/60 mt-1">{t('opening_balance.bank_balance', { balance: formatBaht(openingBalance.data.bank_balance) })}</p>
                      )}
                    </td>
                    <td className="hidden md:table-cell text-sm text-base-content/60">
                      {account.name_th || '—'}
                    </td>
                    <td className="hidden md:table-cell">
                      <Badge
                        size="sm"
                        variant={accountTypeVariants[account.account_type] || 'ghost'}
                      >
                        {t(`account_types.${account.account_type}`)}
                      </Badge>
                    </td>
                    <td className="hidden lg:table-cell text-sm">
                      {t(`normal_balances.${account.normal_balance}`)}
                    </td>
                    <td className="hidden lg:table-cell">
                      <Badge variant={account.active ? 'success' : 'ghost'}>
                        {account.active ? tc('status.active') : tc('status.inactive')}
                      </Badge>
                    </td>
                    <td>
                      {account.code === '1120' && canSetOpening && (
                        /* Below md the label is hidden and the icon stands alone:
                           the text wrapped to four lines in the narrow cell. */
                        <Button
                          size="sm"
                          variant="outline"
                          className="whitespace-nowrap"
                          aria-label={t('opening_balance.title')}
                          title={t('opening_balance.title')}
                          leftIcon={<Landmark className="w-4 h-4" />}
                          onClick={() => setOpeningRequested(true)}
                        >
                          <span className="hidden md:inline">{t('opening_balance.title')}</span>
                        </Button>
                      )}
                      {!account.system && (
                        <div className="flex gap-1 justify-end">
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => openEditModal(account)}
                            aria-label={tc('actions.edit')}
                            leftIcon={<Edit className="w-3 h-3" />}
                          />
                          <Button
                            size="xs"
                            variant="ghost"
                            onClick={() => {
                              setDeletingId(account.id)
                              setShowDeleteModal(true)
                            }}
                            aria-label={tc('actions.delete')}
                            leftIcon={<Trash2 className="w-3 h-3 text-error" />}
                          />
                        </div>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {canSetOpening && openingRequested && <OpeningBalanceModal onClose={closeOpeningModal} />}

      {/* Seed Confirm Modal */}
      <Modal
        isOpen={showSeedModal}
        onClose={() => setShowSeedModal(false)}
        title={t('chart_of_accounts.seed_accounts')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowSeedModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button onClick={handleSeed} loading={seedAccounts.isPending}>
              {tc('actions.confirm')}
            </Button>
          </div>
        }
      >
        <p>{t('chart_of_accounts.seed_confirm')}</p>
      </Modal>

      {/* Delete Confirm Modal */}
      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={tc('actions.delete')}
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowDeleteModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button variant="error" onClick={handleDelete} loading={deleteAccount.isPending}>
              {tc('actions.delete')}
            </Button>
          </div>
        }
      >
        <p>{t('chart_of_accounts.delete_confirm')}</p>
      </Modal>

      {/* Create/Edit Modal */}
      <Modal
        isOpen={showFormModal}
        onClose={() => setShowFormModal(false)}
        title={
          editingAccount
            ? t('chart_of_accounts.title') + ' — ' + tc('actions.edit')
            : t('chart_of_accounts.add_account')
        }
        footer={
          <div className="flex gap-2 justify-end">
            <Button variant="ghost" onClick={() => setShowFormModal(false)}>
              {tc('actions.cancel')}
            </Button>
            <Button
              type="submit"
              form="gl-account-form"
              loading={isSubmitting || createAccount.isPending || updateAccount.isPending}
            >
              {tc('actions.save')}
            </Button>
          </div>
        }
      >
        <form id="gl-account-form" onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <Input
              label={t('labels.code')}
              placeholder={t('placeholders.code')}
              error={errors.code?.message}
              {...register('code')}
            />
            <Select
              label={t('labels.account_type')}
              options={accountTypeOptions}
              error={errors.account_type?.message}
              {...register('account_type')}
            />
          </div>
          <Input
            label={t('labels.name')}
            placeholder={t('placeholders.name')}
            error={errors.name?.message}
            {...register('name')}
          />
          <Input
            label={t('labels.name_th')}
            placeholder={t('placeholders.name_th')}
            {...register('name_th')}
          />
          <div className="grid grid-cols-2 gap-4">
            <Select
              label={t('labels.normal_balance')}
              options={normalBalanceOptions}
              {...register('normal_balance')}
            />
            <Select
              label={t('labels.parent_account')}
              options={accountOptions}
              {...register('parent_id')}
            />
          </div>
          <Checkbox size="sm" label={t('labels.active')} {...register('active')} />
        </form>
      </Modal>
    </div>
  )
}
